/**
 * CopyButton: the detail-card header's copy control, on its own so the Element card's category headings
 * (Typography, Spacing, Size and layout, Appearance) carry the same button with the same states (2026-10-21):
 * Copy01Icon, then Tick02Icon for 1.5s after a successful write; the shared CopyTooltip reads the label on hover
 * and focus, "Copied!" on success, "Copy failed" on a refused write, which also reaches `onCopyError` (the
 * extension shows its toast). `staticCopyText` is the non-hydrated HTML report path: the button carries the
 * payload as `data-copy-element` for the report's shared copy script and renders no React state.
 */
import { type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Copy01Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import { CopyTooltip } from './CopyTooltip';

import { useCopyFeedback } from './useCopyFeedback';

export interface CopyButtonProps {
  /** The button's aria-label and the tooltip's resting text ("Copy typography"). */
  label: string;
  /** Text the button writes, labels and units kept. */
  copyText: () => string;
  /** Clipboard payload for non-hydrated HTML reports, handled by their shared copy script. */
  staticCopyText?: string;
  /** Surface clipboard adapter, e.g. the dashboard’s selection fallback. Reject on failure. */
  writeClipboard?: (text: string) => Promise<void>;
  /** Optional custom decoration; the default supplies the success/error tooltip. */
  wrapCopyButton?: (button: ReactElement, label: string, copied: boolean) => ReactNode;
  /** Report a refused clipboard write (the extension shows a toast). */
  onCopyError?: (err: unknown) => void;
  /** The gesture, for analytics: fires on the click, before the write resolves. */
  onCopy?: () => void;
  className?: string;
}

export function CopyButton({ label, copyText, staticCopyText, writeClipboard, wrapCopyButton, onCopyError, onCopy, className = 'oq-issue-item__copy-btn oq-detail-card__copy' }: CopyButtonProps) {
  const { copy, copied, failed } = useCopyFeedback(async () => {
    const text = copyText();
    if (writeClipboard) await writeClipboard(text);
    else await navigator.clipboard.writeText(text);
  }, err => {
    console.error('[CopyButton] copy failed for', label, err instanceof Error ? err.message : String(err));
    onCopyError?.(err);
  });
  const handleCopy = (e: MouseEvent) => {
    // Inside a <summary> the click must not toggle the disclosure the button sits in.
    e.preventDefault();
    e.stopPropagation();
    onCopy?.();
    void copy();
  };

  const bare = (
    <button type="button" className={className} onClick={handleCopy} aria-label={label} data-copy-element={staticCopyText}>
      <HugeiconsIcon icon={copied ? Tick02Icon : Copy01Icon} size={14} strokeWidth={2} color="currentColor" />
    </button>
  );
  if (staticCopyText !== undefined) return bare;
  if (wrapCopyButton) return <>{wrapCopyButton(bare, label, copied)}</>;
  return <CopyTooltip label={label} copied={copied} failed={failed}>{bare}</CopyTooltip>;
}
